import { IA11yConfiguration } from "@ok/interfaces/a11y/IA11yConfiguration";
import * as a11yConfiguration from 'OK/pms!accessibilityConfiguration';
import { isVisible } from 'OK/a11tyHelper';

/**
 * Модуль для доступности в модальных окнах.
 * Добавляет захват фокуса в модалке (через tab) и возвращение фокуса на элемент, с которого была открыта модалка
 */

const { areAccessibleModalsEnabled } = a11yConfiguration as IA11yConfiguration;

type TOriginalFocusedElement = HTMLElement | (() => HTMLElement | undefined);

interface ILayer {
    modalLayerEl: HTMLElement;
    modalLayerName: string;
    activateFocusOnOriginalElement: () => void;
    // храним только первый и последний элемент, другие не нужны
    borderElements: [HTMLElement, HTMLElement];
}

const ALL_ELEMENTS_SELECTOR = [
    "a[href]:not([disabled]):not([tabindex='-1'])",
    "button:not([disabled]):not([tabindex='-1'])",
    "textarea:not([disabled]):not([tabindex='-1'])",
    "input[type='email']:not([disabled])",
    "input[type='text']:not([disabled])",
    "input[type='radio']:not([disabled])",
    "input[type='checkbox']:not([disabled])",
    "input[type='file']:not([disabled])",
    "select:not([disabled])",
    "[tabindex='0']",
    "[role='button']",
    "[contenteditable='true']"
].join(', ');

const prevActiveLayers: ILayer[] = [];
const MODAL_NAME_ATTRIBUTE = 'data-modal-name';
const MODAL = 'modal';

const getLast = <T>(list: ArrayLike<T>): T => list[list.length - 1];

const getModal = (modalLayerName: string): HTMLElement | null => {
    return document.querySelector(`[${MODAL_NAME_ATTRIBUTE}="${modalLayerName}"]`);
}

function getFocusableEls(modal: HTMLElement): HTMLElement[] {
    const els = modal.querySelectorAll(ALL_ELEMENTS_SELECTOR);
    return Array.prototype.slice.call(els);
}

const getFirstAndLastEls = (modal: HTMLElement, firstElementForFocus?: HTMLElement): [HTMLElement, HTMLElement] => {
    const focusableElements = getFocusableEls(modal);
    let firstFocusableElement = firstElementForFocus || focusableElements[0];
    let counter = 0;

    // выкидываем элементы с hidden родителями
    while ((counter < focusableElements.length) && !isVisible(firstFocusableElement as HTMLElement)) {
        firstFocusableElement = focusableElements[++counter];
    }

    let lastFocusableElement = getLast(focusableElements);
    counter = focusableElements.length - 1;
    while ((counter > 0) && !isVisible(lastFocusableElement as HTMLElement)) {
        lastFocusableElement = focusableElements[--counter];
    }

    return [firstFocusableElement, lastFocusableElement] as [HTMLElement, HTMLElement];
}

export class AccessibleModal {
    activate(el: HTMLElement): void {
        if (!areAccessibleModalsEnabled) {
            return;
        }

        // Не важно, под каким именем нам нужно регистрировать модалку
        // поэтому регистрируем с дефолтным именем
        const modalName = el.getAttribute(MODAL_NAME_ATTRIBUTE) || MODAL;
        if (modalName) {
            AccessibleModal.registerModalByElement(el, modalName);
        }
    }

    deactivate(el: HTMLElement): void {
        if (!areAccessibleModalsEnabled) {
            return;
        }
        const modalName = el.getAttribute(MODAL_NAME_ATTRIBUTE) || MODAL;
        if (modalName) {
            AccessibleModal.removeModal(modalName);
        }
    }

    private static handleTab(event: KeyboardEvent): void {
        if (event.key !== 'Tab') {
            return;
        }
        const [firstFocusableElement, lastFocusableElement] = prevActiveLayers[prevActiveLayers.length - 1].borderElements;

        let activeElement = document.activeElement!;

        if (activeElement.getAttribute('tabindex') === '-1') {
            // вручную установленный фокус каким-то js-модулем, ищем родителя
            const candidate = activeElement.closest(ALL_ELEMENTS_SELECTOR);
            if (candidate != null) {
                activeElement = candidate;
            }
        }

         // при нажатии shift + tab и фокусе на первом элементе нужно перенести фокус на последний элемент
         // если же был нажат шифт и выделен не последний элемент, то делать ничего не нужно
        if (event.shiftKey) {
            if (activeElement === firstFocusableElement){
                lastFocusableElement?.focus();
                event.preventDefault();
            }
            return;
        }

        // при простом нажатии проверяем только то, что был ли последний
        if (activeElement === lastFocusableElement) {
            firstFocusableElement?.focus();
            event.preventDefault();
        }
    }

    /**
     * Регистрирует модалку по имени леера.
     * Чтобы зарегистрировать какой-то элемент как модалку, нужно, чтобы на нем висал data-modal-name атрибут
     * со значением, равным названию текущего леера. Модалки с одинаковыми названиями допускаются, но тогда
     * метод будет просто создавать новые границы для самой верхней модалки на стеке
     * @param modalLayerName
     * @param shouldFocusInModal
     * @param originalActiveElement
     */
    static registerModal(modalLayerName: string, shouldFocusInModal = true, originalActiveElement?: TOriginalFocusedElement, firstElementForFocus?: HTMLElement) : void {
        if (!areAccessibleModalsEnabled) {
            return;
        }
        // если открыли тот же леер, то просто обновляем границы
        if (prevActiveLayers.length
            && getLast(prevActiveLayers).modalLayerName === modalLayerName ) {
            this.refreshCurrentBorders();
            return;
        }

        const modal = getModal(modalLayerName);
        if (!modal) {
            return;
        }

        this.registerModalByElement(modal, modalLayerName || MODAL, shouldFocusInModal, originalActiveElement, firstElementForFocus);
    }

    private static getActivateFocusOnOriginalElement(activeElement: HTMLElement, originalActiveElement?: TOriginalFocusedElement): VoidFunction {
        return () => {
            if (originalActiveElement) {
                if (typeof originalActiveElement === 'function') {
                    (originalActiveElement() || activeElement)?.focus();
                } else {
                    (originalActiveElement || activeElement)?.focus();
                }
            } else {
                activeElement?.focus();
            }
        }
    }

    static registerModalByElement(modal: HTMLElement, modalLayerName: string, shouldFocusInModal?: boolean, originalActiveElement?: TOriginalFocusedElement, firstElementForFocus?: HTMLElement): void {
        if (!areAccessibleModalsEnabled) {
            return;
        }
        if (!prevActiveLayers.length) {
            document.addEventListener("keydown", this.handleTab);
        }
        const borderElements = getFirstAndLastEls(modal, firstElementForFocus);
        const activeElement = document.activeElement as HTMLElement;

        prevActiveLayers.push({
            modalLayerEl: modal,
            modalLayerName: modalLayerName,
            activateFocusOnOriginalElement: this.getActivateFocusOnOriginalElement(activeElement, originalActiveElement),
            borderElements: borderElements
        });

        if (shouldFocusInModal) {
            borderElements[0]?.focus();
        }
    }

    // если контент модалки поменялся, но сама модалка осталась, мы должны пересоздать границы
    static refreshCurrentBorders(): void {
        if (!areAccessibleModalsEnabled) {
            return;
        }
        if (!prevActiveLayers.length) {
            return;
        }
        const lastActiveLayer = getLast(prevActiveLayers);
        const modal = getModal(lastActiveLayer.modalLayerName);
        if (!modal) {
            // по какой-то причине сборщик не может взать логгер через импорт, поэтому так
            window.OK.logging.logger.error('cant-find-last-modal', `last active modal ${lastActiveLayer.modalLayerName}`);
            return;
        }

        lastActiveLayer.borderElements = getFirstAndLastEls(modal);
    }

    /**
     * Убирает модалку из стека доступных модалок.
     * Важно для восстановления фокуса.
     */
    static removeModal(modalLayerName: string): void {
        if (!areAccessibleModalsEnabled) {
            return;
        }
        if (getLast(prevActiveLayers)?.modalLayerName !== modalLayerName) {
            return;
        }
        const currentLayer = prevActiveLayers.pop();

        if (currentLayer) {
            currentLayer.activateFocusOnOriginalElement();
            if (!prevActiveLayers.length) {
                document.removeEventListener("keydown", AccessibleModal.handleTab);
            }
        }
    }
}
